import React from "react";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import DangerIconSvg from "../assets/images/icons/DangerIconSvg";
import {
  ControlpanelCancelButton,
  ControlpanelErrorButton,
  ControlpanelPrimaryButton,
} from "./StyledButton";
import { SpinnerRing } from "./CustomSpinner";

const VARIANT_STYLES = {
  warning: {
    iconColor: "#EAB308",
    innerCircle: "#FEF9C3",
    outerCircle: "#FEFCE8",
  },
  delete: {
    iconColor: "#D00D00",
    innerCircle: "#FEE2E2",
    outerCircle: "#FEF2F2",
  },
};

/**
 * Confirm dialog với 2 style: warning | delete
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => void} props.onConfirm
 * @param {string} props.title
 * @param {string} props.subtitle
 * @param {'warning' | 'delete'} [props.variant='warning']
 * @param {string} [props.cancelText]
 * @param {string} [props.confirmText]
 * @param {boolean} [props.loading=false]
 */
const ConfirmActionDialog = ({
  open = false,
  onClose,
  onConfirm,
  title,
  subtitle,
  variant = "warning",
  cancelText,
  confirmText,
  loading = false,
}) => {
  const { t } = useTranslation();
  const style = VARIANT_STYLES[variant] || VARIANT_STYLES.warning;
  const isDelete = variant === "delete";

  const handleConfirm = () => {
    if (loading) return;
    onConfirm?.();
  };

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const ConfirmButton = isDelete
    ? ControlpanelErrorButton
    : ControlpanelPrimaryButton;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      disableEscapeKeyDown={loading}
      keepMounted
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          position: "relative",
          overflow: "hidden",
          boxShadow:
            "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
          margin: "16px",
        },
      }}
    >
      {loading ? (
        <div className="absolute inset-0 z-[20] flex items-center justify-center bg-white/60 rounded-2xl">
          <SpinnerRing loading size={50} color="#3B82F6" />
        </div>
      ) : null}

      <DialogContent sx={{ padding: "24px 24px 0 24px" }}>
        <div
          className="flex items-center justify-center rounded-full"
          style={{
            width: 56,
            height: 56,
            backgroundColor: style.outerCircle,
          }}
        >
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              width: 40,
              height: 40,
              backgroundColor: style.innerCircle,
            }}
          >
            <DangerIconSvg color={style.iconColor} width={24} height={24} />
          </div>
        </div>

        <p className="mt-4 text-lg font-semibold text-[#111827] leading-7">
          {title}
        </p>

        {subtitle && (
          <p className="mt-1 text-sm text-[#6B7280] leading-5">{subtitle}</p>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: "24px",
          gap: 1,
          display: "flex",
          width: "100%",
          "& > *": {
            flex: 1,
            width: "100%",
            justifyContent: "center",
          },
        }}
      >
        <ControlpanelCancelButton onClick={handleClose} disabled={loading}>
          <p>{cancelText || t("button.cancel")}</p>
        </ControlpanelCancelButton>

        <ConfirmButton onClick={handleConfirm} disabled={loading}>
          <p>
            {confirmText ||
              (isDelete ? t("button.delete") : t("button.confirm"))}
          </p>
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmActionDialog;
